import { computed, defineComponent, nextTick, ref, watch } from "vue";
import StepBox from "../../components/StepBox";
import InputText from "../../forms/InputText";
import Utils from "../../utils/Utils";
import InputCheckbox from "../../forms/InputCheckbox";
import Validator, { ValidationError } from "../../utils/Validator";
import InputRadio from "../../forms/InputRadio";
import InputSelect from "../../forms/InputSelect";
import Expander from "../../components/Expander";
import OfferUtils from "../../utils/OfferUtils";
import Tracking from "../../utils/Tracking";

const InputBigCheck = defineComponent({
    components: {
        InputCheckbox,
    },

    template: `
        <div class="InputBigCheck">
            <div class="InputBigCheck__inner">
                <div class="InputBigCheck__input">
                    <InputCheckbox :title="title" :single="single" :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" />
                </div>

                <div v-if="info" class="InputBigCheck__info" v-html="info"></div>
            </div>
        </div>
    `,

    emits: [
        'update:modelValue',
    ],

    props: {
        modelValue: { type: [Number, Boolean], require: true },
        single: { type: Boolean, default: true },

        title: { type: String, require: true },
        info: { type: String },
    },
});

export default defineComponent({
    components: {
        InputText,
        InputCheckbox,
        InputRadio,
        InputSelect,
        StepBox,
        Expander,
        InputBigCheck,
    },

    template: `
        <StepBox class="StepFormContents"
            :step="2"
            :currentstep="step"
            title="Dane osobowe i zgody"

            sticky="auto"

            :valid="true"
            :error="sent ? options.valid_consents_error : null"

            @edit="$emit('goto', 2)"
            @back="$emit('goto', 1)"
            @next="onNext"
        >
            <div class="StepFormContents__form">
                <h3>Dane osobowe</h3>

                <InputText label='<span class="requiredfield">*</span>Imię' v-model="form.billing_firstname" :required="true" :error="errors.billing_firstname" />
                <InputText label='<span class="requiredfield">*</span>Nazwisko' v-model="form.billing_lastname" :required="true" :error="errors.billing_lastname" />
                <InputText label='<span class="requiredfield">*</span>PESEL' v-model="form.pesel" :required="true" :disabled="form.foreign" :error="errors.pesel" />

                <InputCheckbox title="Nie jestem obywatelem PL" v-model="form.foreign" />

                <template v-if="form.foreign">
                    <InputText label='<span class="requiredfield">*</span>Numer paszportu' v-model="form.passport" :required="true" :error="errors.passport" />
                </template>

                <hr>

                <h3>Dane kontaktowe</h3>

                <InputText label='<span class="requiredfield">*</span>Numer telefonu' v-model="form.phone" :required="true" mask="000 000 000" :error="errors.phone" />
                <InputText label='<span class="requiredfield">*</span>Adres e-mail' v-model="form.email" :required="true" :error="errors.email" />

                <hr>

                <h3>Oświadczenie</h3>

                <InputSelect label='<span class="requiredfield">*</span>Energia elektryczna będzie dostarczana na potrzeby:' :items="options.si_buildingtypes" hint="wybierz" v-model="form.buildingtype" :error="errors.buildingtype" />
                <InputText v-if="form.buildingtype == 100" v-model="form.buildingtype_other" placeholder="inne jakie?" :error="errors.buildingtype_other" />

                <InputRadio label='<span class="requiredfield">*</span>Oświadczam, że posiadam tytuł prawny do obiektu, gdzie będzie dostarczana energia elektryczna i wynika on z:' :items="options.si_legaltitles" v-model="form.legaltitle" :error="errors.legaltitle" />
                <InputText v-if="form.legaltitle == 100" v-model="form.legaltitle_other" placeholder="inny jaki?" :error="errors.legaltitle_other" />

                <hr>

                <h3>Zgody</h3>

                <p>Poniżej znajdziesz zgody i oświadczenia, o których wyrażenie Cię prosimy. Zgody obowiązkowe są niezbędne do realizacji zamówienia. Zapoznaj się z ich treścią, zanim je zaakceptujesz.</p>

                <h4>Zgody niezbędne do realizacji zamówienia</h4>

                <div class="StepFormContents__sub">
                    <InputCheckbox :title="label_terms_rules" v-model="form.terms_rules" :error="errors.terms_rules" />
                    <InputCheckbox title='<span class="requiredfield">*</span>Chcę otrzymywać mailem informacje o mojej umowie i zmianach do umowy' v-model="form.terms_contract" :error="errors.terms_contract" />
                </div>

                <hr>

                <h4>Przyspieszenie realizacji</h4>
                <div class="StepFormContents__sub">
                    <InputCheckbox class="StepFormContents__boldcheck" title="Chcę korzystać z usług Respect Energy jak najszybciej po podpisaniu umowy" v-model="form.terms_notice" />

                    <div class="StepFormContents__sub">
                        <p class="StepFormContents__legend">Wyrażam zgodę na natychmiastowe rozpoczęcie usług i przyjmuję do wiadomości utratę prawa do odstąpienia od umowy zgodnie z art. 38 ustawy o prawach konsumenta.</p>
                    </div>
                </div>

                <hr>

                <h4>e-Faktura</h4>
                <div class="StepFormContents__sub">
                    <InputCheckbox class="StepFormContents__boldcheck" title="Zgoda na e-fakturę" v-model="form.terms_einvoice" :info="options.fees?.einvoice ? 'Wyrażając zgodę na e-fakturę, opłata handlowa zostanie pomniejszona o ' + priceFormat(options.fees?.einvoice) + '.' : null" />

                    <div class="StepFormContents__sub">
                        <p v-if="options.fees?.einvoice" class="StepFormContents__legend">Wyrażając zgodę na e-fakturę, <b>opłata handlowa zostanie pomniejszona o {{ priceFormat(options.fees?.einvoice) }}</b>.</p>
                    </div>
                </div>

                <hr>

                <h4>Zgody na komunikację marketingową</h4>
                <div class="StepFormContents__sub">
                    <InputCheckbox class="StepFormContents__boldcheck" title="Zaznacz wszystkie po zapoznaniu się z treścią zgód" :modelValue="terms_trade > 0" :single="terms_trade == 3" @update:modelValue="onTerms('terms_trade')" :info="options.fees?.trade ? 'Wyrażając wszystkie zgody na komunikację marketingową, opłata handlowa zostanie pomniejszona o kolejne ' + priceFormat(options.fees?.trade) : null" />

                    <Expander :expanded="false" class="StepFormContents__sub StepFormContents__termslast" label_expand="rozwiń" label_collapse="zwiń" type="up">
                        <p v-if="options.fees?.trade" class="StepFormContents__legend">Wyrażając wszystkie zgody na komunikację marketingową, <b>opłata handlowa zostanie pomniejszona o kolejne {{ priceFormat(options.fees?.trade) }}</b></p>

                        <hr>

                        <p>Wyrażam zgodę na otrzymywanie od Respect Energy S.A informacji dotyczących produktów i usług Respect Energy S.A za pośrednictwem:</p>

                        <div class="StepFormContents__termslast-group">
                            <InputCheckbox title="e-mail" v-model="form.terms_trade_email" />
                            <InputCheckbox title="SMS" v-model="form.terms_trade_sms" />
                            <InputCheckbox title="telefon" v-model="form.terms_trade_phone" />
                        </div>

                        <hr>

                        <p>Wyrażam zgodę na otrzymywanie od Respect Energu S.A. informacji dotyczących produktów i usług oferowanych przez podmioty trzecie, o których Respect Energy S.A. informuje w związku ze świadczeniem swoich usług za pośrednictwem:</p>

                        <div class="StepFormContents__termslast-group">
                            <InputCheckbox title="e-mail" v-model="form.terms_trade_others_email" />
                            <InputCheckbox title="SMS" v-model="form.terms_trade_others_sms" />
                            <InputCheckbox title="telefon" v-model="form.terms_trade_others_phone" />
                        </div>
                    </Expander>
                </div>

                <hr>

                <p class="StepFormContents__required">Pola oznaczone czerwoną gwiazdką * są wymagane</p>
            </div>
        </StepBox>
    `,

    emits: [
        'next',
        'goto',
    ],

    props: {
        /** @var OrderJson */
        options: { type: Object, required: true },
        step: { type: Number, required: true },
    },

    setup(props, ctx) {
        const errors_active = ref(false);
        const sent = ref(false);

        const form = ref({
            billing_firstname: <string>null,
            billing_lastname: <string>null,

            pesel: <string>null,
            foreign: <boolean>null,
            passport: <string>null,

            phone: <string>null,
            email: <string>null,

            legaltitle: <number>null,
            legaltitle_other: <string>null,

            buildingtype: <number>null,
            buildingtype_other: <string>null,

            contract_email: <string>null,

            terms_rules: <number>props.options.terms,
            terms_trade_email: <number>null,
            terms_trade_sms: <number>null,
            terms_trade_phone: <number>null,

            terms_trade_others_email: <number>null,
            terms_trade_others_sms: <number>null,
            terms_trade_others_phone: <number>null,

            terms_einvoice: <number>null,
            terms_contract: <number>null,
            terms_notice: <number>null,
        });

        const errors = ref({
            billing_firstname: computed(() => inputError('billing_firstname')),
            billing_lastname: computed(() => inputError('billing_lastname')),
            pesel: computed(() => inputError('pesel')),
            passport: computed(() => inputError('passport')),
            phone: computed(() => inputError('phone')),
            email: computed(() => inputError('email')),
            buildingtype: computed(() => inputError('buildingtype')),
            buildingtype_other: computed(() => inputError('buildingtype_other')),
            legaltitle: computed(() => inputError('legaltitle')),
            legaltitle_other: computed(() => inputError('legaltitle_other')),
            terms_rules: computed(() => inputError('terms_rules')),
            terms_contract: computed(() => inputError('terms_contract')),
        });

        const phone_filtered = computed(() => {
            return form.value.phone?.replace(/\s+/g, '');
        });

        const valid = computed(() => {
            try {
                Validator.assertNames(form.value.billing_firstname);
                Validator.assertNames(form.value.billing_lastname);

                if (form.value.foreign) {
                    Validator.assertRequired(form.value.passport);
                } else {
                    Validator.assertPesel(form.value.pesel);
                }

                Validator.assertEmail(form.value.email);
                Validator.assertPhone(phone_filtered.value);

                Validator.assertRequired(form.value.legaltitle);

                if (form.value.legaltitle == 100) {
                    Validator.assertRequired(form.value.legaltitle_other);
                }

                Validator.assertRequired(form.value.buildingtype);

                if (form.value.buildingtype == 100) {
                    Validator.assertRequired(form.value.buildingtype_other);
                }

                if (!form.value.terms_rules || !form.value.terms_contract) {
                    return false;
                }

                return true;
            } catch (e) {
                return false;
            }
        });

        const terms_all = computed(() => {
            return (terms_trade.value == 3 ? 1 : 0) + (terms_required_all.value == 2 ? 1 : 0);
        })

        const terms_trade = computed(() => {
            return (terms_trade_all.value / 2) + (terms_trade_others_all.value / 2);
        })

        const terms_trade_all = computed(() => {
            return form.value.terms_trade_email + form.value.terms_trade_sms + form.value.terms_trade_phone;
        })

        const terms_trade_others_all = computed(() => {
            return form.value.terms_trade_others_email + form.value.terms_trade_others_sms + form.value.terms_trade_others_phone;
        })

        const terms_required_all = computed(() => {
            return form.value.terms_rules + form.value.terms_contract;
        })

        const label_terms_rules = computed(() => {
            const r = ['<span class="requiredfield">*</span>Akceptuję'];

            const codeitem = props.options.code ? OfferUtils.getOfferCodeItem(props.options.code, props.options.offer?.id) : null;
            const terms = OfferUtils.getOfferTerms(props.options.offer, codeitem);

            for (const term of terms) {
                r.push(`<a href="${term.url}" target="_blank">${term.title}</a>`);
            }

            return r.join(', ');
        })

        function inputError(field: string) {

            try {
                field == 'billing_firstname' && (form.value.billing_firstname || errors_active.value) && Validator.assertNames(form.value.billing_firstname);
                field == 'billing_lastname' && (form.value.billing_lastname || errors_active.value) && Validator.assertNames(form.value.billing_lastname);

                field == 'pesel' && (form.value.pesel || errors_active.value) && Validator.assertPesel(form.value.pesel);

                if (form.value.foreign) {
                    field == 'passport' && (form.value.passport || errors_active.value) && Validator.assertRequired(form.value.passport);
                }

                field == 'email' && (form.value.email || errors_active.value) && Validator.assertEmail(form.value.email);
                field == 'phone' && (form.value.phone || errors_active.value) && Validator.assertPhone(phone_filtered.value);

                field == 'legaltitle' && (form.value.legaltitle || errors_active.value) && Validator.assertRequired(form.value.legaltitle);

                if (form.value.legaltitle == 100) {
                    field == 'legaltitle_other' && (form.value.legaltitle_other || errors_active.value) && Validator.assertRequired(form.value.legaltitle_other);
                }

                field == 'buildingtype' && (form.value.buildingtype || errors_active.value) && Validator.assertRequired(form.value.buildingtype);

                if (form.value.buildingtype == 100) {
                    field == 'buildingtype_other' && (form.value.buildingtype_other || errors_active.value) && Validator.assertRequired(form.value.buildingtype_other);
                }

                field == 'terms_rules' && errors_active.value && Validator.assertRequired(form.value.terms_rules);
                field == 'terms_contract' && errors_active.value && Validator.assertRequired(form.value.terms_contract);
            } catch (e) {
                if (e instanceof ValidationError) {
                    return e.message;
                }

                return 'Błędny format pola';
            }
        }

        function priceFormat(val: number) {
            return Utils.priceFormat(val, false, ' zł', 2);
        }

        function setData() {
            form.value.billing_firstname = props.options.billing_firstname;
            form.value.billing_lastname = props.options.billing_lastname;

            form.value.foreign = props.options.foreign;
            form.value.pesel = props.options.pesel;
            form.value.passport = props.options.passport;

            form.value.phone = props.options.phone;
            form.value.email = props.options.email;

            form.value.legaltitle = props.options.legaltitle;
            form.value.legaltitle_other = props.options.legaltitle_other;

            form.value.buildingtype = props.options.buildingtype;
            form.value.buildingtype_other = props.options.buildingtype_other;

            form.value.contract_email = props.options.contract_email;

            form.value.terms_trade_email = props.options.terms_trade_email;
            form.value.terms_trade_sms = props.options.terms_trade_sms;
            form.value.terms_trade_phone = props.options.terms_trade_phone;

            form.value.terms_trade_others_email = props.options.terms_trade_other_email;
            form.value.terms_trade_others_sms = props.options.terms_trade_other_sms;
            form.value.terms_trade_others_phone = props.options.terms_trade_other_phone;

            form.value.terms_einvoice = props.options.terms_einvoice;
            form.value.terms_contract = props.options.terms_contract;
            form.value.terms_notice = props.options.terms_notice;
        }

        function onTerms(key: string, v: any = null): void {
            switch (key) {
                case 'terms_all':
                    if (v === null) {
                        v = !terms_all.value;
                    }

                    onTerms('terms_trade', v);
                    onTerms('terms_required_all', v);
                    break;

                case 'terms_trade':
                    if (v === null) {
                        v = !terms_trade_all.value;
                    }

                    onTerms('terms_trade_all', v);
                    onTerms('terms_trade_others_all', v);
                    break;

                case 'terms_trade_all':
                    if (v === null) {
                        v = !terms_trade_all.value;
                    }

                    form.value.terms_trade_email = v;
                    form.value.terms_trade_sms = v;
                    form.value.terms_trade_phone = v;
                    break;

                case 'terms_trade_others_all':
                    if (v === null) {
                        v = !terms_trade_others_all.value;
                    }

                    form.value.terms_trade_others_email = v;
                    form.value.terms_trade_others_sms = v;
                    form.value.terms_trade_others_phone = v;
                    break;

                case 'terms_required_all':
                    if (v === null) {
                        v = !terms_required_all.value;
                    }

                    onTerms('terms_rules', v);
                    onTerms('terms_contract', v);
                    break;

                case 'terms_rules':
                    if (v === null) {
                        v = !form.value.terms_rules;
                    }

                    form.value.terms_rules = v;
                    break;

                case 'terms_einvoice':
                    if (v === null) {
                        v = !form.value.terms_einvoice;
                    }

                    form.value.terms_einvoice = v;
                    break;

                case 'terms_contract':
                    if (v === null) {
                        v = !form.value.terms_contract;
                    }

                    form.value.terms_contract = v;
                    break;

                case 'terms_notice':
                    if (v === null) {
                        v = !form.value.terms_notice;
                    }

                    form.value.terms_notice = v;
                    break;
            }
        }

        async function onNext() {
            if (!valid.value) {
                console.log('error');

                errors_active.value = true;

                nextTick(() => {

                    const $first = $('.InputWidget--error, .InputCheckbox--error').first();
                    Utils.scrollToEl($first);
                })
                return;
            }

            await Utils.setCart('consents', {
                billing_firstname: form.value.billing_firstname,
                billing_lastname: form.value.billing_lastname,

                foreign: form.value.foreign,
                pesel: form.value.pesel,
                passport: form.value.passport,

                email: form.value.email,
                phone: phone_filtered.value,

                legaltitle: form.value.legaltitle,
                legaltitle_other: form.value.legaltitle_other,

                buildingtype: form.value.buildingtype,
                buildingtype_other: form.value.buildingtype_other,

                contract_email: form.value.contract_email,

                terms_trade_email: form.value.terms_trade_email,
                terms_trade_sms: form.value.terms_trade_sms,
                terms_trade_phone: form.value.terms_trade_phone,

                terms_trade_other_email: form.value.terms_trade_others_email,
                terms_trade_other_sms: form.value.terms_trade_others_sms,
                terms_trade_other_phone: form.value.terms_trade_others_phone,

                terms_einvoice: form.value.terms_einvoice,
                terms_contract: form.value.terms_contract,
                terms_notice: form.value.terms_notice,
            });

            sent.value = true;

            if (props.options.valid_consents) {
                Tracking.doEvent('click_personaldata_step2');

                ctx.emit('next');
            }
        }

        watch(props.options, () => {
            setData();
        });

        setData();

        return {
            errors_active,
            sent,
            form,
            errors,

            valid,
            terms_all,
            terms_trade,
            terms_trade_all,
            terms_trade_others_all,
            terms_required_all,
            label_terms_rules,

            priceFormat,
            setData,
            onTerms,
            onNext,
        };
    },
});
